import Avatar from "shared/Avatar/Avatar";
import Label from "./Label";
import Input from "shared/Input/Input";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { Fragment } from "react";
import { Transition, Dialog } from "@headlessui/react";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import { useTranslation } from "react-i18next";

import useProfileEdit from "hooks/useProfileEdit";
import { Checkbox, FormControlLabel } from "@mui/material";
import TermsAndCondition from "./TermsAndCondition";
import TermsAndConditionContent from "./TermsAndConditionContent";

export default function ProfileEdit() {
  const {
    firstName,
    lastName,
    email,
    phone,
    instagram,
    image,
    isUploading,
    isShowModal,
    isFormValid,
    isOpenTerms,
    missingFields,
    setFirstName,
    setLastName,
    setEmail,
    setPhone,
    setInstagram,
    _onUserImageUpload,
    _onOpen,
    _onOpenTerms,
    _onClose,
    _onSubmit,
  } = useProfileEdit();

  const { t } = useTranslation();

  const renderPointHistory = () => {
    return (
      <Transition appear show={isShowModal} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={_onClose}>
          <div className="min-h-screen px-4 text-center">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-40" />
            </Transition.Child>

            <span className="inline-block h-screen align-middle" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95">
              <div className="inline-block py-8 h-screen w-full max-w-4xl">
                <div className="inline-flex pb-2 flex-col w-full text-left align-middle transition-all transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-900 dark:border dark:border-neutral-700 dark:text-neutral-100 h-full">
                  <div className="relative flex-shrink-0 px-6 py-4  text-center">
                    <h3 className="text-lg font-medium leading-6 text-gray-900" id="headlessui-dialog-title-70">
                      {t(isOpenTerms ? "all_terms" : "profile_update_title")}
                    </h3>
                    <span className="absolute left-3 top-3">
                      <ButtonClose onClick={_onClose} />
                    </span>
                  </div>
                  {isOpenTerms ? (
                    <TermsAndConditionContent />
                  ) : (
                    <div className="px-8 overflow-auto text-neutral-700 dark:text-neutral-300 divide-y divide-neutral-200">
                      <div className="space-y-6 sm:space-y-8">
                        <>
                          <h2 className="text-3xl font-semibold">{t("profile_update_title_big")}</h2>
                          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
                          <div className="flex flex-col md:flex-row">
                            <div className="flex-shrink-0 flex items-start">
                              <div className="relative rounded-full overflow-hidden flex">
                                <Avatar imgUrl={String(image)} sizeClass="w-32 h-32" />
                                <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center text-neutral-50 cursor-pointer">
                                  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      d="M17.5 5H7.5C6.83696 5 6.20107 5.26339 5.73223 5.73223C5.26339 6.20107 5 6.83696 5 7.5V20M5 20V22.5C5 23.163 5.26339 23.7989 5.73223 24.2678C6.20107 24.7366 6.83696 25 7.5 25H22.5C23.163 25 23.7989 24.7366 24.2678 24.2678C24.7366 23.7989 25 23.163 25 22.5V17.5M5 20L10.7325 14.2675C11.2013 13.7988 11.8371 13.5355 12.5 13.5355C13.1629 13.5355 13.7987 13.7988 14.2675 14.2675L17.5 17.5M25 12.5V17.5M25 17.5L23.0175 15.5175C22.5487 15.0488 21.9129 14.7855 21.25 14.7855C20.5871 14.7855 19.9513 15.0488 19.4825 15.5175L17.5 17.5M17.5 17.5L20 20M22.5 5H27.5M25 2.5V7.5M17.5 10H17.5125"
                                      stroke="currentColor"
                                      strokeWidth={1.5}
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>

                                  <span className="mt-1 text-xs">{t("profile_update_change_image")}</span>
                                </div>
                                <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={_onUserImageUpload} accept="image/png, image/gif, image/jpeg" multiple={false} />
                              </div>
                            </div>
                            <div className="flex-grow mt-10 md:mt-0 md:pl-16 max-w-3xl space-y-6 ">
                              <div style={{ display: "flex", gap: "1rem" }}>
                                <div>
                                  <Label>{t("profile_update_first_name")}</Label>
                                  <Input className="mt-1.5" value={String(firstName || "")} onChange={(e) => setFirstName(e.target.value)} />
                                </div>
                                {/* ---- */}
                                <div>
                                  <Label>{t("profile_update_last_name")}</Label>
                                  <Input className="mt-1.5" value={String(lastName || "")} onChange={(e) => setLastName(e.target.value)} />
                                </div>
                              </div>
                              {/* ---- */}
                              <div>
                                <Label>{t("profile_update_email")}</Label>
                                <Input className="mt-1.5" value={String(email || "")} onChange={(e) => setEmail(e.target.value)} />
                              </div>
                              <div>
                                <Label>{t("profile_update_phone")}</Label>
                                <Input
                                  className="mt-1.5"
                                  type="number"
                                  value={phone !== null && phone !== undefined ? String(phone) : ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setPhone(value === "" ? null : Number(value));
                                  }}
                                />
                              </div>

                              <div>
                                <Label>Instagram</Label>
                                <Input className="mt-1.5" value={String(instagram || "")} onChange={(e) => setInstagram(e.target.value)} prefix="@" />
                              </div>

                              <div className="pt-2 pb-30">
                                {isFormValid ? (
                                  <>
                                    <span className="text-sm text-neutral-600">
                                      * {t("profile_accept_terms") + " "}
                                      <a style={{ textDecoration: "underline" }} className="text-sm text-neutral-600" onClick={_onOpenTerms}>
                                        {t("all_terms")}
                                      </a>
                                    </span>
                                    <br />
                                    <ButtonPrimary className="mt-5" loading={isUploading} disabled={isUploading} onClick={_onSubmit}>
                                      {t("profile_update_btn")}
                                    </ButtonPrimary>
                                  </>
                                ) : (
                                  <span className="text-sm text-neutral-500 block mb-5" aria-required>
                                    *{" "}
                                    {t("profile_update_missing_fields", {
                                      fields: missingFields.join(", "),
                                    })}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    );
  };

  return (
    <>
      {renderPointHistory()}
      <ButtonSecondary onClick={_onOpen}>
        <i className="text-xl las la-user-edit"></i>
        <span className="ml-3">{t("profile_update_title")}</span>
      </ButtonSecondary>
    </>
  );
}
